/**
 * Simple utility to track and log Firebase query count metrics
 */

// Store query counts by collection
const queryCounters: Record<string, number> = {};
// Store timestamp of last query by collection
const lastQueryTime: Record<string, number> = {};
// Store total query counts by session (since page load)
let totalQueries = 0;
// Store total query counts in the last 60 seconds
let recentQueries = 0;
// Store query timestamps for recent tracking
const queryTimestamps: number[] = [];

/**
 * Track a new query for the specified collection
 * @param collection The Firestore collection being queried
 */
export function trackQuery(collection: string): void {
  // Increment total counter
  totalQueries++;
  
  // Increment collection-specific counter
  queryCounters[collection] = (queryCounters[collection] || 0) + 1;
  
  // Update last query time
  lastQueryTime[collection] = Date.now();
  
  // Track for recent queries calculation
  queryTimestamps.push(Date.now());
  
  // Update recent queries count (last 60 seconds)
  const sixtySecondsAgo = Date.now() - 60000;
  while (queryTimestamps.length > 0 && queryTimestamps[0] < sixtySecondsAgo) {
    queryTimestamps.shift();
  }
  recentQueries = queryTimestamps.length;
  
  // Log query info if it's getting excessive
  if (queryCounters[collection] % 5 === 0 || recentQueries > 10) {
    logQueryMetrics();
  }
}

/**
 * Log current query metrics to console
 */
export function logQueryMetrics(): void {
  console.warn("[QueryTracker] Firebase Query Metrics:", {
    total: totalQueries,
    last60Seconds: recentQueries,
    byCollection: queryCounters
  });
  
  // Alert if we're seeing excessive queries
  if (recentQueries > 20) {
    console.error("[QueryTracker] ALERT: Excessive Firebase queries detected!");
  }
}

/**
 * Reset all query counters
 */
export function resetQueryCounters(): void {
  Object.keys(queryCounters).forEach(key => {
    queryCounters[key] = 0;
  });
  totalQueries = 0;
  recentQueries = 0;
  queryTimestamps.length = 0;
}

/**
 * Get the current query metrics
 */
export function getQueryMetrics(): {
  total: number;
  recent: number;
  byCollection: Record<string, number>;
  lastQueryTimes: Record<string, number>;
} {
  return {
    total: totalQueries,
    recent: recentQueries,
    byCollection: { ...queryCounters },
    lastQueryTimes: { ...lastQueryTime }
  };
} 